import { isAxiosError } from 'axios';
import { parseBuffer } from 'music-metadata';
import pRetry from 'p-retry';
import { CreditApi, CreditTransactionType } from 'subscription-service-client';

import {
    VoiceListResponse,
    type VoiceoverApiVoiceoverGetVoicesPostRequest,
    type VoiceCloneResponse,
} from '@videoblocks/ml-text-service-client';
import { LoggerFactory } from '@videoblocks/typescript-logger';

import { voiceoverApi } from './mlTextServiceConfig';

export type { VoiceCloneResponse } from '@videoblocks/ml-text-service-client';

const logger = LoggerFactory.create('VoiceoverApi');

const getAudioDuration = async (file: File): Promise<number> => {
    const arrayBuffer = await file.arrayBuffer();
    const buffer = Buffer.from(arrayBuffer);

    const metadata = await parseBuffer(buffer, { mimeType: file.type });
    return metadata.format.duration // duration in seconds
        ? Math.ceil(metadata.format.duration)
        : 0;
};

export enum VoiceoverModelId {
    ElevenMultilingualV2 = 'eleven_multilingual_v2',
    ElevenFlashV2 = 'eleven_flash_v2',
    ElevenV3 = 'eleven_v3',
}

export const SUPPORTED_MODEL_IDS = new Set(Object.values(VoiceoverModelId));
export const DEFAULT_MODEL_ID = VoiceoverModelId.ElevenV3;

const PER_UNIT_COST = 0.075;

/**
 * Calculates the cost of a voiceover operation.
 *
 * @param {string | number | File} subject - The input to calculate cost for. If a string, cost is per character. If a number or File (audio), cost is per second of audio duration.
 * @param {string} _modelId - The ElevenLabs model ID. Reserved for future per-model pricing.
 * @returns {Promise<number>} The negative number of credits to deduct, rounded up to the nearest whole credit.
 *
 * Cost calculation logic:
 * - For text: cost is the number of characters in the string.
 * - For audio: cost is the duration in seconds (rounded up). Duration may be passed as a number (if calculated client-side), or the file
 * - The cost is multiplied by a per-unit cost, then rounded up to the nearest whole credit, and made negative for deduction.
 */
async function calculateCost(subject: string | number | File, _modelId: string = DEFAULT_MODEL_ID): Promise<number> {
    let cost = 0;
    if (typeof subject === 'string') {
        cost = subject.length;
    } else if (typeof subject === 'number') {
        cost = subject;
    } else {
        cost = await getAudioDuration(subject);
    }

    return Math.ceil(cost * PER_UNIT_COST) * -1; // negative for deduction
}

const createVoiceClone = async (file: File, accountCode: string): Promise<VoiceCloneResponse> => {
    try {
        const response = await voiceoverApi.voiceoverCreateVoicePost({
            files: [file],
            userId: accountCode,
            elevenlabsVoiceName: [accountCode, new Date().toISOString()].join('-'),
            elevenlabsRemoveBackgroundNoise: false,
        });

        return response.data;
    } catch (error) {
        if (isAxiosError(error)) {
            throw new Error(`HTTP error! status: ${error.response?.status}`);
        } else {
            logger.error({ error }, 'Error in createVoiceClone');
            throw error;
        }
    }
};

const speechToSpeech = async (
    file: File,
    elevenlabsVoiceId: string,
    accountCode: string,
): Promise<ReadableStream<Uint8Array> | null> => {
    try {
        const response = await voiceoverApi.voiceoverSpeechToSpeechPost(
            {
                file,
                userId: accountCode,
                elevenlabsVoiceId,
            },
            {
                responseType: 'stream',
            },
        );

        const amount = await calculateCost(file);

        void tryPostingCreditTransaction(accountCode, amount);

        return response.data as ReadableStream;
    } catch (error) {
        if (isAxiosError(error)) {
            throw new Error(`HTTP error! status: ${error.response?.status}`);
        } else {
            logger.error({ error }, 'Error in speechToSpeech');
            throw error;
        }
    }
};

const textToSpeech = async (
    text: string,
    voiceId: string,
    speed: number = 1,
    accountCode: string,
    modelId: string = DEFAULT_MODEL_ID,
): Promise<ReadableStream<Uint8Array> | null> => {
    try {
        const response = await voiceoverApi.voiceoverTextToSpeechPost(
            {
                textToSpeechRequest: {
                    user_id: accountCode,
                    text,
                    algo_params: {
                        elevenlabs: {
                            voice_id: voiceId,
                            speed,
                            model_id: modelId,
                        },
                    },
                },
            },
            {
                responseType: 'stream',
            },
        );

        const amount = await calculateCost(text, modelId);

        void tryPostingCreditTransaction(accountCode, amount);

        return response.data as ReadableStream;
    } catch (error) {
        if (isAxiosError(error)) {
            logger.error(
                {
                    status: error.response?.status,
                    responseBody: error.response ? JSON.stringify(error.response.data) : 'No response body',
                },
                'HTTP error in textToSpeech',
            );
            return null;
        }
        logger.error({ error }, 'Error in textToSpeech');
        return null;
    }
};

const tryPostingCreditTransaction = async (accountCode: string, amount: number): Promise<number | void> => {
    return pRetry(
        async () =>
            CreditApi.postTransaction(accountCode, {
                amount,
                transactionType: CreditTransactionType.AiVoiceover,
            }),
        {
            onFailedAttempt: (error) => {
                logger.warn(
                    {
                        error,
                        attemptNumber: error.attemptNumber,
                        retriesLeft: error.retriesLeft,
                    },
                    'Credit transaction attempt failed',
                );
            },
            retries: 3,
            factor: 2,
            minTimeout: 1000,
        },
    ).catch((error) => {
        logger.error({ error }, 'Failed to post credit transaction after multiple attempts');
    });
};

export type VerifiedLanguage = {
    language: string;
    model_id: string;
    accent: string | null;
    locale: string | null;
    preview_url: string;
};

export type Voice = {
    voice_id: string;
    name: string;
    category: string;
    accent: string;
    descriptive: string;
    age: string;
    gender: string;
    language: string;
    use_case: string;
    description: string;
    preview_url: string;
    is_pvc: boolean; // true if the voice is a Professional Voice Contributor (PVC)
    verified_languages: VerifiedLanguage[];
};

/**
 * Returns the list of available voices from the ML text service API.
 *
 * @returns {Promise<Voice[]>} The list of available voices
 * @throws {Error} If the API request fails or returns invalid data
 */
const getListOfVoices = async (): Promise<Voice[]> => {
    const voiceParam: VoiceoverApiVoiceoverGetVoicesPostRequest = {
        voiceListRequest: {},
    };

    try {
        const voiceList = await voiceoverApi.voiceoverGetVoicesPost(voiceParam);
        const response: VoiceListResponse = voiceList.data;
        const voices: unknown = response.voices;

        if (!voices || !Array.isArray(voices)) {
            logger.error({ voices }, 'Voices data is null, undefined, or not an array');
            throw new Error('Invalid voices data received from API');
        }

        return voices as Voice[];
    } catch (error) {
        logger.error({ error }, 'Error fetching voices');
        throw error;
    }
};

export const VoiceoverApi = {
    createVoiceClone,
    speechToSpeech,
    textToSpeech,
    getListOfVoices,
    calculateCost,
};
